import axios from 'axios'
import { getSession, getSettings, getUser } from '../_utils/get.store'
import { store } from '../_store'
import { authActions } from '../_store/auth/auth.slice'
import { getPlatform } from '../_utils/platform'
import { checkoutActions } from '../_store/checkout/checkout.slice'

export const request = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
})
// this instance is added because it works when catching request's interceptors | request itself didn't work
const refreshInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
})

let isRefreshing = false
let refreshSubscribers: ((token: string | null) => void)[] = []

const onRefreshed = (token: string | null) => {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

const addRefreshSubscriber = (cb: (token: string | null) => void) => {
  refreshSubscribers.push(cb)
}

request.interceptors.request.use((config) => {
  config.headers = config.headers || {}

  if (typeof window !== 'undefined') {
    if (!config.headers['Authorization']) {
      const user = getUser()
      if (user?.access_token) {
        config.headers['Authorization'] = user.access_token
      }
    }

    if (!config.headers['Shipper']) {
      const settings = getSettings()
      const session = getSession()

      if (settings?.shipper_id) {
        config.headers['Shipper'] = settings.shipper_id
      } else if (session?.shipper_id) {
        config.headers['Shipper'] = session.shipper_id
      }
    }
  }

  if (!config.headers['Platform']) {
    const platformId = getPlatform()

    config.headers['Platform'] = platformId
  }

  return config
})

request.interceptors.response.use(
  (response) => response,
  (error) => errorHandler(error)
)

const errorHandler = async (error: any) => {
  const originalRequest = error.config

  if (error.response && typeof window !== 'undefined') {
    const { status } = error.response

    if ((status === 401 || status === 403) && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((token: string | null) => {
            if (token) {
              originalRequest.headers['Authorization'] = token
              originalRequest._retry = true
              resolve(request(originalRequest))
            } else {
              reject(error)
            }
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const user = getUser()
        const refresh_token = user?.refresh_token

        if (!refresh_token) {
          console.log('no refresh token')
          return Promise.reject(error)
        }

        const { data } = await refreshInstance.post(
          '/v1/customers/refresh-token',
          {
            refresh_token,
          }
        )

        store.dispatch(
          authActions.updateTokens({
            access_token: data.access_token,
            refresh_token: data.refresh_token,
          })
        )

        originalRequest.headers['Authorization'] = data.access_token

        onRefreshed(data.access_token)

        isRefreshing = false

        return request(originalRequest)
      } catch (error) {
        isRefreshing = false

        onRefreshed(null)

        store.dispatch(authActions.logout())
        store.dispatch(checkoutActions.clear())

        if (process.env.NODE_ENV === 'development') {
          console.error('❌ Refresh Token Error:', error)
        }

        return Promise.reject(error)
      }
    }
  }

  if (process.env.NODE_ENV === 'development') {
    console.error('❌ API Error:', error)
  }

  return Promise.reject(error.response || error)
}
